import { FC, useCallback, useEffect } from "react";
import { useParams } from "react-router";

import { DigipostEntryPoint } from "../../../types/entrypoint";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../analytics/siteimprove";
import { useEntrypointContext } from "../EntrypointContext";
import { remove } from "../infrastructure/digipost-fetch";
import { useRootPath } from "../infrastructure/hooks/useRootPath";
import { findURIWithWindowHostname } from "../infrastructure/link";
import SpinnerContainer from "./spinnerContainer/SpinnerContainer";

export const OAuthLogout: FC = () => {
  const entrypoint: DigipostEntryPoint = useEntrypointContext();
  const { redirectUrl } = useParams();
  const rootPath = useRootPath();

  const logout = useCallback(async (): Promise<void> => {
    if (redirectUrl === undefined) {
      throw new Error("Missing redirect URL!");
    } else {
      const decodedRedirectUrl = decodeURIComponent(redirectUrl);
      const redirAsUrl = new URL(decodedRedirectUrl);

      const allowedRedirects = [
        "no.posten.Digipost://oauthredirect",
        "dp://no.digipost.android/oauthredirect",
        "no.posten.Digipost.Test://oauthredirect",
        "no.posten.Digipost.difitest://oauthredirect",
      ];

      let redirectAllowed = false;
      for (const allowedRedir of allowedRedirects) {
        const allowedRedirAsUrl = new URL(allowedRedir);
        if (
          redirAsUrl.protocol === allowedRedirAsUrl.protocol &&
          redirAsUrl.host === allowedRedirAsUrl.host
        ) {
          redirectAllowed = true;
          break;
        }
      }

      if (!redirectAllowed) return;

      try {
        const logoutUrl = findURIWithWindowHostname("logout", entrypoint.link);

        const extraHeader = {
          FINAL_DESTINATION: `${rootPath}/mobileRedirect/${encodeURIComponent(
            decodedRedirectUrl,
          )}`,
        };

        const logoutResponse = await remove(
          logoutUrl,
          entrypoint.csrfToken,
          {},
          extraHeader,
        );

        const idPortenLogoutLocation = logoutResponse.headers.get("Location");

        if (idPortenLogoutLocation != null) {
          siteImproveEvent({
            action: SiteImproveActions.LOGOUT_WITH_IDPORTEN_LOGOUT,
            category: SiteImproveCategories.LOGOUT_PRIVATE_USER,
            label: "logger ut av IDPorten",
          });
          window.location.href = idPortenLogoutLocation;
        } else {
          window.location.href = decodedRedirectUrl;
        }
      } catch (error) {
        window.location.href = decodedRedirectUrl;
      }
    }
  }, [entrypoint, redirectUrl]);

  useEffect(() => {
    logout();
  }, [logout]);

  return <SpinnerContainer />;
};
